import { agentRunFinishReasonCopy } from "@common/constants/domains/agentRuns/agentRunCopy";
import type { AgentRunSummary } from "@common/schemas/domains/agentRuns/agent-run.schema";
import type { TaskActivityPayload } from "@common/schemas/domains/agentRuns/task-activity.schema";
import { AgentRunState, AgentRunWaitKind } from "@common/types/agentRun";
import { pluralizeWithCount } from "@common/helpers/string";

export type AgentRunStatusKind =
    | "done"
    | "cancelled"
    | "failed"
    | "expired"
    | "paused"
    | "pausing"
    | "waiting"
    | "running";

/** accent pulses (the run is working now); the others are static dots. */
export type AgentRunDotTone = "accent" | "warning" | "error" | "neutral";

export interface AgentRunStatus {
    kind: AgentRunStatusKind;
    dot: AgentRunDotTone;
    label: string;
    tooltip: string;
}

type StatusRun = Pick<
    AgentRunSummary,
    "state" | "pausedAt" | "progress" | "waitingOn" | "finishReason"
>;

// Finished states win over any pause metadata, so a row that ended while
// paused never reads as paused.
export function getAgentRunStatusKind(
    run: Pick<StatusRun, "state" | "pausedAt" | "progress">,
): AgentRunStatusKind {
    if (
        run.state !== AgentRunState.running &&
        run.state !== AgentRunState.waiting
    ) {
        return run.state;
    }
    if (run.pausedAt !== null) {
        return run.progress?.step === "paused" ? "paused" : "pausing";
    }
    return run.state;
}

// Continue wakes answer and status waits; a proposal needs a real reply, so a
// run parked only on proposals has nothing for Continue to do.
export function isWaitingOnProposalsOnly(
    run: Pick<StatusRun, "state" | "pausedAt" | "progress" | "waitingOn">,
): boolean {
    return (
        getAgentRunStatusKind(run) === "waiting" &&
        run.waitingOn.length > 0 &&
        run.waitingOn.every((entry) => entry.kind === AgentRunWaitKind.proposal)
    );
}

export function getAgentRunStatus(run: StatusRun): AgentRunStatus {
    const kind = getAgentRunStatusKind(run);
    switch (kind) {
        case "done":
            return { kind, dot: "neutral", label: "Done", tooltip: "Done" };
        case "cancelled":
            return {
                kind,
                dot: "neutral",
                label: "Stopped",
                tooltip: "Stopped",
            };
        case "failed": {
            const reason = agentRunFinishReasonCopy(run.finishReason);
            return {
                kind,
                dot: "error",
                label: "Failed",
                tooltip: reason === null ? "Failed" : `Failed: ${reason}`,
            };
        }
        case "expired":
            return { kind, dot: "error", label: "Expired", tooltip: "Expired" };
        case "paused":
            return { kind, dot: "warning", label: "Paused", tooltip: "Paused" };
        case "pausing":
            return {
                kind,
                dot: "warning",
                label: "Pausing…",
                tooltip: "Pausing after the current step",
            };
        case "waiting": {
            const label = `Waiting on ${pluralizeWithCount("task", run.waitingOn.length)}`;
            return { kind, dot: "warning", label, tooltip: label };
        }
        case "running":
            return {
                kind,
                dot: "accent",
                label: "Running",
                tooltip: "Running",
            };
    }
}

export const FINISHED_KINDS: ReadonlySet<AgentRunStatusKind> = new Set([
    "done",
    "cancelled",
    "failed",
    "expired",
]);

export const PAUSABLE_KINDS: ReadonlySet<AgentRunStatusKind> = new Set([
    "running",
]);

export const CONTINUABLE_KINDS: ReadonlySet<AgentRunStatusKind> = new Set([
    "waiting",
    "pausing",
    "paused",
]);

export const STOPPABLE_KINDS: ReadonlySet<AgentRunStatusKind> = new Set([
    "running",
    "waiting",
    "pausing",
    "paused",
    "failed",
    "expired",
]);

export function isCurrentStateEvent({
    status,
    firstWait,
    event,
}: {
    status: Pick<AgentRunStatus, "kind">;
    firstWait: Pick<AgentRunSummary["waitingOn"][number], "taskId"> | null;
    event: TaskActivityPayload;
}): boolean {
    switch (status.kind) {
        case "waiting":
            return (
                event.eventType === "waiting" &&
                firstWait !== null &&
                event.metadata.waitedOnTaskId === firstWait.taskId
            );
        case "done":
        case "failed":
        case "expired":
            return event.eventType === "run_finished";
        case "cancelled":
            return (
                event.eventType === "run_stopped" ||
                event.eventType === "run_finished"
            );
        case "paused":
        case "pausing":
            return event.eventType === "run_paused";
        case "running":
            return false;
    }
}
